<template>
  <div>
    <component
      :is="layout"
      :order-edit-log="['wasabi', 'ova-restaurant'].includes(web) ? orderEditLog : null"
      @close-order-edit-log="orderEditLog = null"
    >
      <slot />
    </component>  
    <WaiterFab 
      v-if="showWaiter"
      @call-waiter="CallWaiter" 
      /> 
    <LoginRequiredModal
      :isOpen="dishesStore.showLoginRequired"
      @close="dishesStore.showLoginRequired = false"
    />
  </div>
</template>

<script setup lang="ts">
import WaiterFab from '~/components/waiter/WaiterFab.vue'
import LayoutWasabi from '~/layouts/wasabi.vue'
import defFavicon from '@/assets/images/ova-restaurant/ova_logo_2.svg'
import defFaviconLight from '@/assets/images/ova-restaurant/ova_logo.svg'
import LayoutOva from '~/layouts/ova-restuarant.vue'
import LoginRequiredModal from '~/components/LoginRequiredModal.vue'

import axios from 'axios'
import { ref, nextTick } from 'vue'
import { useHead } from '#imports'
import { useWindowSize } from '@vueuse/core'

const route = useRoute()
const localePath = useLocalePath()
const { width } = useWindowSize()
const userStore = useUserStore()
const { user } = storeToRefs(userStore)

const isDisabled = ref(false)
const { setBodyAttr, getCurrentWeb } = useClient()
const restaurantStore = useRestaurantStore()
const restaurantViewModeStore = useRestaurantViewModeStore()
const dishesStore = useDishesStore()
const { slug, id, favicon, tableId, experience, isLoadingExperience, menuIds, service_method } = storeToRefs(restaurantStore)
const { getCustomerExperience, fetchQRcode } = restaurantStore
const isLoadingRestaurant = ref(true)

const web = ref('')
const isInitialized = ref(false)
const orderEditLog = ref<any>(null)
const getRestaurantTitle = () => {
  if(experience.value?.browser_tab_name || experience.value?.name) return experience.value?.browser_tab_name ?? experience.value?.name;

  return 'OVA Restaurant'
}

const updateRestaurantMetaDetails = () => {
  if(!experience.value) return;

  if(experience.value.meta_title) {
    let ogTitle = document.querySelector("meta[property='og:title']");
    if (ogTitle) {
      ogTitle.setAttribute('content', experience.value.meta_title);
    } else {
      ogTitle = document.createElement('meta');
      ogTitle.setAttribute('property', 'og:title');
      ogTitle.setAttribute('content', experience.value.meta_title);
      document.head.appendChild(ogTitle);
    }
  }

  if(experience.value.meta_description) {
    let metaDesc = document.querySelector("meta[name='description']");
    if (metaDesc) {
      metaDesc.setAttribute('content', experience.value.meta_description);
    } else {
      metaDesc = document.createElement('meta');
      metaDesc.setAttribute('name', 'description');
      metaDesc.setAttribute('content', experience.value.meta_description);
      document.head.appendChild(metaDesc);
    }

    let ogDesc = document.querySelector("meta[property='og:description']");
    if (ogDesc) {
      ogDesc.setAttribute('content', experience.value.meta_description);
    } else {
      ogDesc = document.createElement('meta');
      ogDesc.setAttribute('property', 'og:description');
      ogDesc.setAttribute('content', experience.value.meta_description);
      document.head.appendChild(ogDesc);
    }
  }
}

const faviconLinks = computed(() => {
  const customFavicon = experience.value?.favicon
  
  if (customFavicon) {
    const isSvg = customFavicon.endsWith('.svg') || customFavicon.startsWith('data:image/svg+xml')
    return [
      {
        rel: 'icon',
        type: isSvg ? 'image/svg+xml' : undefined,
        href: customFavicon
      }
    ]
  } else {
    return [
      {
        rel: 'icon',
        type: 'image/svg+xml',
        media: '(prefers-color-scheme: light)',
        href: defFaviconLight
      },
      {
        rel: 'icon',
        type: 'image/svg+xml',
        media: '(prefers-color-scheme: dark)',
        href: defFavicon
      }
    ]
  }
})

useHead({
  meta: [
    {
      name: 'description',
      content: () => experience.value?.meta_description || 'Digital Food Menu App'
    },
    {
      property: 'og:title',
      content: () => experience.value?.meta_title || getRestaurantTitle()
    },
    {
      property: 'og:description',
      content: () => experience.value?.meta_description || 'Digital Food Menu App'
    }
  ],
  link: faviconLinks
})

import { useWebsocket } from '@/composables/useWebsocket'

const showWaiter = computed(() => {
  return experience.value?.enable_waiter && web.value != 'wasabi' && !isLoadingRestaurant.value && !delivery_mode_enabled.value
})

const delivery_mode_enabled = computed(() => {
  return service_method.value.includes('delivery');
})


onMounted(async () => {
  let qrCode = Array.isArray(route.params.id)
  ? route.params.id[0]
  : route.params.id;
  let qrCodeResponse: any = null
  
  await restaurantStore.getDataFromStorage()

  if(!qrCode) {
    qrCode = id.value || useSafeStorage('_id') || '';
    if(qrCode) {
      qrCodeResponse = await fetchQRcode(qrCode);
    }
  }

  if (qrCodeResponse?.data?.order_mode) {
    restaurantViewModeStore.setModeFromApi(qrCodeResponse.data.order_mode)
    restaurantStore.syncViewModeWithAvailability()
  }
  
  userStore.getDataFromStorage()
  const dishesStore = useDishesStore()
  dishesStore.getDataFromStorage()

  const setExperienceData = () => {
    dishesStore.iso_code = experience.value?.currency?.iso_code || 'USD';
    dishesStore.symbol = experience.value?.currency?.symbol || '$';
  }

  await Promise.all([
    restaurantStore.paymentsMethods(),
    getCustomerExperience()
  ])

  if(experience.value && Object.keys(experience.value).length) {
    setTimeout(() => {
      setExperienceData()
    }, 500);
  } else {
    setExperienceData()
  }

  const { $axios, $i18n } = useNuxtApp()
  const { connect } = useWebsocket()
  const e = connect()
  if (!e) return

  const activeChannels = new Set();
  const activeOrderEditChannels = new Set<string>();

  const subscribeToChannel = (channelName: string, eventName: string) => {
    if (activeChannels.has(channelName)) return;

    e.channel(channelName)
     .listen(eventName, (data: any) => {
        handleStatusUpdate(data);
     });
    
    activeChannels.add(channelName);
  };

  const handleStatusUpdate = (data: any) => {
    if (data.status === 'canceled' || data.status === 'cancelled') {
      persistCanceledOrderSnapshot(data.order_id);
    }

    if(dishesStore.orders && dishesStore.orders.length) {
      const updatedOrder = dishesStore.orders.find((el) => (el?.id || el?.orderID) == data.order_id);
      
      if(updatedOrder) {
        typeof updatedOrder?.status === 'string' ? updatedOrder.status = data.status : updatedOrder.status.slug = data.status;
      }
    }

    if(dishesStore.showingSingleOrder && (dishesStore.showingSingleOrder?.id == data.order_id || dishesStore.showingSingleOrder?.orderID == data.order_id)) {
      if (typeof dishesStore.showingSingleOrder.status === 'string') {
        dishesStore.showingSingleOrder.status = data.status;
      } else if (dishesStore.showingSingleOrder.status && typeof dishesStore.showingSingleOrder.status === 'object') {
        dishesStore.showingSingleOrder.status.slug = data.status;
      } else {
        dishesStore.showingSingleOrder.status = { slug: data.status };
      }
    }

    if(dishesStore.lastOrder && (dishesStore.lastOrder.id || dishesStore.lastOrder.orderID) == data.order_id) {
      if (typeof dishesStore.lastOrder.status === 'string') {
        dishesStore.lastOrder.status = data.status;
      } else if (dishesStore.lastOrder.status && typeof dishesStore.lastOrder.status === 'object') {
        dishesStore.lastOrder.status.slug = data.status;
      } else {
        dishesStore.lastOrder.status = { slug: data.status };
      }
      useSetSafeStorage('lastOrder', JSON.stringify(dishesStore.lastOrder ?? []))
    }

    if(dishesStore.ordersIds && dishesStore.ordersIds[slug.value]) {
      const updatedOrder = dishesStore.ordersIds[slug.value].find((el) => Object.keys(el)[0] == data.order_id);
      if(updatedOrder) {
        if(dishesStore.ordersIds[slug.value].length > 20 && data.status === 'done') {
          dishesStore.ordersIds[slug.value] = dishesStore.ordersIds[slug.value].filter((order) => Object.keys(order)[0] !== data.order_id)
          if(dishesStore.lastOrder && dishesStore.lastOrder.orderID == data.order_id) {
            dishesStore.getLastOrder();
          }
        } else {
          updatedOrder[data.order_id] = data.status;
        }
        useSetSafeStorage('ordersIds', JSON.stringify(dishesStore.ordersIds ?? {}))
      }
    }
  }

  const normalizeCanceledOrderSnapshot = (order: any) => {
    if (!order) return null;

    const normalized = normalizeOrderPayload(order);
    const status = normalized.status && typeof normalized.status === 'object'
      ? { ...normalized.status, slug: 'canceled' }
      : { slug: 'canceled' };

    return {
      ...normalized,
      status,
    };
  }

  const findLocalOrderSnapshot = (orderId: any) => {
    const sameOrder = (order: any) => (order?.id || order?.orderID) == orderId;

    if (dishesStore.showingSingleOrder && sameOrder(dishesStore.showingSingleOrder)) {
      return dishesStore.showingSingleOrder;
    }

    const orderFromList = dishesStore.orders?.find(sameOrder);
    if (orderFromList) return orderFromList;

    if (dishesStore.lastOrder && sameOrder(dishesStore.lastOrder)) {
      return dishesStore.lastOrder;
    }

    return null;
  }

  const persistCanceledOrderSnapshot = (orderId: any) => {
    const order = normalizeCanceledOrderSnapshot(findLocalOrderSnapshot(orderId));
    if (!order) return;

    const stored = useSafeStorage('canceledOrders');
    let canceledOrders: Record<string, Record<string, any>> = {};

    if (stored) {
      try {
        canceledOrders = JSON.parse(stored);
      } catch (error) {
        canceledOrders = {};
      }
    }

    const slugKey = slug.value || 'default';
    canceledOrders[slugKey] = {
      ...(canceledOrders[slugKey] ?? {}),
      [String(orderId)]: order,
    };

    useSetSafeStorage('canceledOrders', JSON.stringify(canceledOrders));
  }

  const handleTransactionUpdate = (data: any) => {
    dishesStore.transactionVersion = (dishesStore.transactionVersion ?? 0) + 1;
  }

  const normalizeOrderPayload = (order: any) => {
    if (!order) return null;

    return {
      ...order,
      dish_order_linking: order.dish_order_linking ?? order.order_dishes ?? [],
    }
  }

  const syncEditedOrder = async (order: any) => {
    if (!order?.id) return;

    const sameOrder = (item: any) => (item?.id || item?.orderID) == order.id;

    if (order.type === 'delivery') {
      const slugKey = slug.value || 'default';
      dishesStore.deliveryOrdersIds[slugKey] = dishesStore.deliveryOrdersIds[slugKey] || [];
      
      const isAlreadyInDelivery = dishesStore.deliveryOrdersIds[slugKey].some(
        obj => Object.keys(obj)[0] == order.id
      );

      if (!isAlreadyInDelivery) {
        const statusVal = typeof order.status === 'string' ? order.status : (order.status?.slug ?? 'created');
        dishesStore.deliveryOrdersIds[slugKey].push({ [order.id]: statusVal });
        useSetSafeStorage('deliveryOrdersIds', JSON.stringify(dishesStore.deliveryOrdersIds ?? {}));
      }
    }

    await nextTick()

    if (dishesStore.orders && dishesStore.orders.length) {
      const index = dishesStore.orders.findIndex(sameOrder);
      if (index !== -1) {
        dishesStore.orders.splice(index, 1, {
          ...dishesStore.orders[index],
          ...order,
        });
      }
    }

    if (dishesStore.showingSingleOrder && sameOrder(dishesStore.showingSingleOrder)) {
      dishesStore.showingSingleOrder = {
        ...dishesStore.showingSingleOrder,
        ...order,
      };
    }

    if (dishesStore.lastOrder && sameOrder(dishesStore.lastOrder)) {
      dishesStore.lastOrder = {
        ...dishesStore.lastOrder,
        ...order,
      };
      useSetSafeStorage('lastOrder', JSON.stringify(dishesStore.lastOrder ?? null));
    }
  }

  const handleOrderDishesEdited = async (data: any) => {
    let editedOrder = null;

    try {
      const response = await $axios.get('/restaurant/order-by-id', {
        params: {
          lang: $i18n.locale.value,
          id: data.order_id,
        },
      });

      editedOrder = normalizeOrderPayload(response.data?.data ?? response.data);
      syncEditedOrder(editedOrder);
    } catch (error) {
      console.error('Failed to fetch edited order:', error);
    }

    orderEditLog.value = {
      ...data,
      order: editedOrder,
      receivedAt: Date.now(),
    };
  }

  const subscribeToOrderEditChannel = (channelName: string) => {
    if (activeOrderEditChannels.has(channelName)) return;

    e.channel(channelName)
      .listen('.OrderDishesEdited', handleOrderDishesEdited);

    activeOrderEditChannels.add(channelName);
  }

  const setupListener = () => {
    if(service_method.value.includes('dine_in') && !tableId.value) {
      setTimeout(() => {
        setupListener()
      }, 500);
      return;
    }

    if (tableId.value) {
      subscribeToChannel(`order-status.${tableId.value}`, '.ChangeOrderStatus');

      e.channel(`order-status.${tableId.value}`)
       .listen('.OrderTransactionUpdated', handleTransactionUpdate)
       .listen('.DeleteOrder', (data: any) => {
         handleStatusUpdate({ order_id: data.order_id, status: 'canceled' });
       });
      subscribeToOrderEditChannel(`order-status.${tableId.value}`);
    }

    if (dishesStore.deliveryOrdersIds && dishesStore.deliveryOrdersIds[slug.value]) {
      dishesStore.deliveryOrdersIds[slug.value].forEach(obj => {
        const orderId = Object.keys(obj)[0];

        subscribeToChannel(`order-status.delivery.${orderId}`, '.ChangeDeliveryOrderStatus');
        
        e.channel(`order-status.delivery.${orderId}`)
          .listen('.OrderTransactionUpdated', handleTransactionUpdate);
        subscribeToOrderEditChannel(`order-status.delivery.${orderId}`);
      });
    }

    watch(() => dishesStore.deliveryOrdersIds?.[slug.value], (newOrders) => {
      if (newOrders && Array.isArray(newOrders)) {
        newOrders.forEach(obj => {
          const orderId = Object.keys(obj)[0];

          subscribeToChannel(`order-status.delivery.${orderId}`, '.ChangeDeliveryOrderStatus');
          
          e.channel(`order-status.delivery.${orderId}`)
            .listen('.OrderTransactionUpdated', handleTransactionUpdate);
          subscribeToOrderEditChannel(`order-status.delivery.${orderId}`);
        });
      }
    }, { deep: true });
  }

  setupListener();

//  restaurantStore.getDataFromStorage()
  await nextTick()

  setBodyAttr()
  web.value = getCurrentWeb()
  document.title = getRestaurantTitle()
  updateRestaurantMetaDetails()
  isLoadingRestaurant.value = false;
  renewActivaty()
  isInitialized.value = true
  document.addEventListener('touchstart', renewActivaty);
})

onUnmounted(() => {
  document.removeEventListener('touchstart', renewActivaty);
})

watch(
  [width, () => route.path, user, isInitialized],
  ([newWidth, newPath, newUser, newInit]) => {
    if (process.client && newInit && newWidth >= 1024 && !newUser) {
      const blockedPathsOnDesktop = ['/profile', '/cart', '/checkout']
      const isBlocked = blockedPathsOnDesktop.some(
        (p) => newPath === localePath(p) || newPath.startsWith(localePath(p) + '/')
      )
      if (isBlocked) {
        dishesStore.showLoginRequired = true
        navigateTo(localePath('/categories'))
      }
    }
  },
  { immediate: true }
)

watch(
  () => route.params,
  async () => {
    if (process.client) {

      web.value = getCurrentWeb()
      document.title = getRestaurantTitle()
      await nextTick()
      setBodyAttr()
    }
  },
  { immediate: true, deep: true }
)
watch(menuIds,
  async (newVal)=>{
    await getCustomerExperience();
    const dishesStore = useDishesStore()
    dishesStore.iso_code = experience.value.currency?.iso_code;
    dishesStore.symbol = experience.value.currency?.symbol;
  }, {deep: true}
)

watch(
  slug,
  async (newVal) => {
    // await restaurantStore.fetchFavicon()
 
    if (process.client) {
      if (newVal === 'wasabi') {
        web.value = newVal
        await nextTick()
        setBodyAttr()
      } else {
        web.value = 'ova-restaurant'
        await nextTick()
        setBodyAttr()
      }

      if (newVal) {
        let authSlug = useSafeStorage('auth_restaurant_slug')
        if (!authSlug && useSafeStorage('access_token')) {
          const storedSlug = useSafeStorage('_slug')
          if (storedSlug) {
            useSetSafeStorage('auth_restaurant_slug', storedSlug)
            authSlug = storedSlug
          }
        }

        if (authSlug && authSlug !== newVal) {
          useRemoveSafeStorage('user')
          useRemoveSafeStorage('access_token')
          userStore.setUser(null)
        }
      }

      document.title = getRestaurantTitle()
      isLoadingRestaurant.value = false;
    }
  },
  { immediate: false }
)

function CallWaiter(data: any) {
  const config = useRuntimeConfig();
  
  const apiUrl = new URL(config.public.apiBase);
  apiUrl.pathname = 'api/waiter/call';
  
  const requestBody = {
    restaurant_slug: slug.value,
    table_number: tableId.value,
    pay: data.pay,
  };
  
  axios.post(apiUrl.href, requestBody)
    .then(response => {
      console.log('Request successful!', response.data);
    })
    .catch(error => {
      console.error('Request failed:', error);
    });
}

const renewActivaty = () => {
  const timestamp = Date.now()
  useSetSafeStorage('qrScanTime', timestamp.toString())
}

const layout = computed(() => {
  if (web.value === 'ova-restaurant') return LayoutOva
  return LayoutWasabi
})

onMounted(() => setBodyAttr())
</script>

<style scoped>
.button-container {
  position: fixed;
  bottom: 50px;
  right: 20px;
  background-color: #ff7b2c;
  border-radius: 100%;
  padding: 13px;
  cursor: pointer;
  z-index: 10;
  transition: background-color 0.3s;
}

.button-container .timer {
  position: absolute;
  top: 50%;
  left: 50%;
  font-weight: 600;
  color: #ffffff;
  font-size: 20px;
  transform: translate(-50%, -50%);
  backdrop-filter: blur(3px);
  width: 100%;
  height: 100%;
  border-radius: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.button-container.disabled {
  background-color: rgb(80 92 120 / 1);
  cursor: not-allowed;
  pointer-events: none;
}

.button-container.disabled::before,
.button-container.disabled::after {
  content: '';
  position: absolute;
  color: transparent;
  border-radius: 100%;
  animation: pulse 0.5s linear 200ms infinite;
  width: 100%;
  left: 0;
  top: 0;
  height: 100%;
  min-height: 45px;
}

@keyframes pulse {
  0% {
    box-shadow: 0 0 2px 1px #ff7b2c;
    transform: scale(0.76);
    opacity: 0;
  }
  50% {
    box-shadow: 0 0 5px 3px #ff7b2c;
    opacity: 1;
  }
  80% {
    box-shadow: 0 0 3px 7px #ff7b2c;
    opacity: 0;
  }
  100% {
    box-shadow: 0 0 0 5px #ff7b2c;
    opacity: 0;
    transform: scale(1.002);
  }
}
</style>
